// Shared account-profile query (TanStack Query owns server data — see lib/queryClient.ts).
// One ["profile"] cache entry feeds every chrome consumer (the AccountMenu avatar, future
// header bits) and ProfilePage invalidates it on save so edits reflect everywhere live.

import { useQuery } from "@tanstack/react-query";
import type { Theme } from "@perlab/ui";
import { isLocalDevMockAuth } from "../../lib/auth/localDevMock";

export interface AccountProfile {
  subject_id: string;
  email?: string | null;
  preferred_username?: string | null;
  name?: string | null;
  display_name?: string | null;
  effective_display_name: string;
  theme_preference?: Theme | null;
  last_scope?: string | null;
  // Base64 data URL of the user's avatar, or null → render initials.
  avatar_data_url?: string | null;
}

export const PROFILE_QUERY_KEY = ["profile"] as const;

async function fetchProfile(): Promise<AccountProfile> {
  const res = await fetch("/api/workspace/profile", { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as AccountProfile;
}

/** Read the current user's profile from the shared cache. */
export function useProfile() {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    // The Windows local mock shell has no account-profile database. Identity still comes from the
    // signed dev session and theme still comes from the design-system cookie, so avoid a guaranteed
    // PostgreSQL timeout without changing any normal runtime behavior.
    enabled: !isLocalDevMockAuth(),
  });
}
